/**
 * Unsent DEPOSIT_NOTICE / DAMAGES_NOTICE rows — MCL 554.603 / 554.609. This is
 * the review surface, not the alert: the move-out escalation ladder
 * (`jobs/notice-escalation.ts`) already pushes on the same data at 14/21/25
 * days. This tab just makes it easy to see everything at once.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import { humanizeEnum } from '../../../components/Form.js';

interface ComplianceClockRow {
  noticeId: string;
  leaseId: string;
  propertyName: string;
  unitLabel: string;
  kind: string;
  triggeredOn: string;
  dueBy: string;
  daysElapsed: number;
  daysUntilDue: number;
}

const columns: Column<ComplianceClockRow>[] = [
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'unit', header: 'Unit', render: (r) => r.unitLabel },
  { key: 'kind', header: 'Notice', render: (r) => humanizeEnum(r.kind) },
  { key: 'triggered', header: 'Triggered', render: (r) => r.triggeredOn },
  { key: 'due', header: 'Due by', render: (r) => r.dueBy },
  {
    key: 'status',
    header: 'Status',
    render: (r) =>
      r.daysUntilDue < 0 ? (
        <span className="pill pill--late">{Math.abs(r.daysUntilDue)}d overdue</span>
      ) : (
        <span className={r.daysUntilDue <= 5 ? 'pill pill--late' : 'pill'}>
          {r.daysUntilDue}d left
        </span>
      ),
  },
  {
    key: 'actions',
    header: '',
    render: (r) => <Link to={`/staff/leases/${r.leaseId}`}>Open lease →</Link>,
  },
];

export function ComplianceClocks({
  ownerId,
  onOwnerChange,
  tenantId,
  onTenantChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
  tenantId: string;
  onTenantChange: (value: string) => void;
}) {
  const [propertyId, setPropertyId] = useState('');

  const { data, isLoading, error } = useList<ComplianceClockRow>({
    resource: 'reports/compliance-clocks',
    pagination: { mode: 'off' },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'ownerId', operator: 'eq', value: ownerId || undefined },
      { field: 'tenantId', operator: 'eq', value: tenantId || undefined },
    ],
  });

  return (
    <div className="stack">
      <p className="muted">
        Unsent deposit and damages notices, most urgent first. Timing is attorney-reviewed
        (invariant 7) — this tab surfaces it, it does not draft the notice or change the clock.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={onOwnerChange}
        tenantId={tenantId}
        onTenantChange={onTenantChange}
        exportPath="/reports/compliance-clocks"
        exportParams={{
          propertyId: propertyId || undefined,
          ownerId: ownerId || undefined,
          tenantId: tenantId || undefined,
        }}
      />

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.noticeId}
        isLoading={isLoading}
        error={error}
        emptyMessage="Nothing outstanding."
      />
    </div>
  );
}
